/**
 * Svelte stores for grammar checking service
 * Provides reactive state management for the UI
 */

import { writable, derived, get, type Readable, type Writable } from 'svelte/store';
import { KoreanGrammarCore } from '../services/KoreanGrammarCore';
import type {
  SpellCheckResult,
  Correction,
  GrammarCheckSettings,
  BatchProgress,
  CorrectionStateInfo,
  GrammarCheckError,
  AIAnalysisRequest,
  AIAnalysisResult
} from '../types/grammar';

/**
 * Grammar check state
 */
interface GrammarCheckState {
  /** Current text being checked */
  text: string;
  /** Last spell check result */
  result: SpellCheckResult | null;
  /** Whether a check is in progress */
  isChecking: boolean;
  /** Current progress for batch processing */
  progress: BatchProgress | null;
  /** Last error that occurred */
  error: GrammarCheckError | null;
  /** Selected corrections by the user */
  selectedCorrections: Record<number, CorrectionStateInfo>;
  /** Whether AI analysis is running */
  isAnalyzing: boolean;
  /** AI analysis results */
  aiResults: AIAnalysisResult[];
}

/**
 * Default grammar check settings
 */
const DEFAULT_SETTINGS: GrammarCheckSettings = {
  apiKey: '',
  apiHost: '',
  apiPort: 443,
  ignoredWords: [],
  filterSingleCharErrors: true,
  ai: {
    provider: 'openai',
    openaiApiKey: '',
    anthropicApiKey: '',
    googleApiKey: '',
    upstageApiKey: '',
    cerebrasApiKey: '',
    openrouterApiKey: '',
    selectedModel: 'gpt-4',
    contextWindow: 8192,
    showTokenWarning: true,
    tokenWarningThreshold: 1000,
    maxTokens: 2048,
    enabled: false
  },
  batchConfig: {
    maxChunkSize: 4500,
    delayBetweenBatches: 500,
    maxRetries: 3,
    timeout: 30000
  },
  cacheConfig: {
    enabled: true,
    maxEntries: 100,
    ttl: 3600000 // 1 hour
  }
};

/**
 * Create the grammar check stores
 */
function createGrammarStore() {
  // Core service instance
  let grammarCore: KoreanGrammarCore | null = null;

  // Settings store
  const settings = writable<GrammarCheckSettings>(DEFAULT_SETTINGS);

  // State store
  const state = writable<GrammarCheckState>({
    text: '',
    result: null,
    isChecking: false,
    progress: null,
    error: null,
    selectedCorrections: {},
    isAnalyzing: false,
    aiResults: []
  });

  // Derived stores for easy access
  const isChecking = derived(state, $state => $state.isChecking);
  const isAnalyzing = derived(state, $state => $state.isAnalyzing);
  const hasError = derived(state, $state => $state.error !== null);
  const corrections = derived(state, $state => $state.result?.corrections || []);
  const errorCount = derived(state, $state => $state.result?.errorCount || 0);
  const processedText = derived(state, $state => $state.result?.resultOutput || '');
  const progress = derived(state, $state => $state.progress);

  // Initialize the grammar core service
  function initialize(initialSettings?: Partial<GrammarCheckSettings>) {
    const finalSettings = { ...DEFAULT_SETTINGS, ...initialSettings };
    settings.set(finalSettings);
    grammarCore = new KoreanGrammarCore(finalSettings);
  }

  // Update settings
  function updateSettings(newSettings: Partial<GrammarCheckSettings>) {
    settings.update(current => {
      const updated = { ...current, ...newSettings };
      if (grammarCore) {
        grammarCore.updateSettings(updated);
      }
      return updated;
    });
  }

  // Check grammar
  async function checkGrammar(text: string): Promise<SpellCheckResult | null> {
    // Clear previous error
    state.update(s => ({ ...s, error: null }));

    // Validate input
    if (!text || !text.trim()) {
      state.update(s => ({
        ...s,
        text: '',
        result: null,
        isChecking: false
      }));
      return null;
    }

    // Initialize core if needed
    if (!grammarCore) {
      initialize();
    }

    // Update state
    state.update(s => ({
      ...s,
      text,
      isChecking: true,
      progress: null
    }));

    try {
      // Perform spell check with progress callback
      const result = await grammarCore!.performSpellCheck(
        text,
        (progress) => {
          state.update(s => ({ ...s, progress }));
        }
      );

      // Update state with result
      state.update(s => ({
        ...s,
        result,
        isChecking: false,
        progress: null,
        selectedCorrections: initializeSelectedCorrections(result.corrections)
      }));

      return result;

    } catch (error) {
      // Handle error
      const grammarError = error as GrammarCheckError;
      state.update(s => ({
        ...s,
        error: grammarError,
        isChecking: false,
        progress: null
      }));

      console.error('Grammar check failed:', error);
      return null;
    }
  }

  // Apply corrections to text
  function applyCorrections(): string {
    const currentState = get(state);
    
    if (!currentState.result || !grammarCore) {
      return currentState.text;
    }

    const correctedText = grammarCore.applyCorrections(
      currentState.text,
      currentState.result.corrections,
      currentState.selectedCorrections
    );

    // Update the text with corrections applied
    state.update(s => ({
      ...s,
      text: correctedText
    }));

    return correctedText;
  }

  // Update selected correction
  function updateSelectedCorrection(
    index: number,
    stateInfo: CorrectionStateInfo
  ) {
    state.update(s => ({
      ...s,
      selectedCorrections: {
        ...s.selectedCorrections,
        [index]: stateInfo
      }
    }));
  }

  // Toggle correction selection
  function toggleCorrection(index: number) {
    state.update(s => {
      const current = s.selectedCorrections[index];
      if (!current) return s;

      const newState = current.currentState === 'corrected' 
        ? 'original-kept' as const
        : 'corrected' as const;

      return {
        ...s,
        selectedCorrections: {
          ...s.selectedCorrections,
          [index]: {
            ...current,
            currentState: newState
          }
        }
      };
    });
  }

  // Add ignored words
  async function addIgnoredWords(words: string[]) {
    if (!grammarCore) {
      initialize();
    }

    await grammarCore!.addIgnoredWords(words);
    
    // Update settings to reflect new ignored words
    settings.update(s => ({
      ...s,
      ignoredWords: [...s.ignoredWords, ...words]
    }));
  }

  // Clear cache
  function clearCache() {
    if (grammarCore) {
      grammarCore.clearCache();
    }
  }

  // Get cache statistics
  function getCacheStats() {
    if (!grammarCore) {
      return null;
    }
    return grammarCore.getCacheStats();
  }

  // Perform AI analysis
  async function analyzeWithAI(
    corrections?: Correction[]
  ): Promise<AIAnalysisResult[]> {
    const currentState = get(state);
    
    if (!grammarCore || !currentState.result) {
      return [];
    }

    state.update(s => ({
      ...s,
      isAnalyzing: true,
      aiResults: []
    }));

    try {
      const request: AIAnalysisRequest = {
        corrections: corrections || currentState.result.corrections,
        contextText: currentState.text,
        userEditedStates: Object.fromEntries(
          Object.entries(currentState.selectedCorrections)
            .filter(([_, info]) => info.isUserEdited)
            .map(([index, info]) => [index, info.selectedValue])
        )
      };

      const results = await grammarCore.performAIAnalysis(request);

      state.update(s => ({
        ...s,
        isAnalyzing: false,
        aiResults: results
      }));

      // Apply AI suggestions to selected corrections
      results.forEach(result => {
        updateSelectedCorrection(result.correctionIndex, {
          correctionIndex: result.correctionIndex,
          currentState: result.isExceptionProcessed ? 'exception-processed' : 'corrected',
          selectedValue: result.selectedValue,
          isExceptionState: result.isExceptionProcessed,
          isUserEdited: false
        });
      });

      return results;

    } catch (error) {
      state.update(s => ({
        ...s,
        isAnalyzing: false,
        error: error as GrammarCheckError
      }));

      console.error('AI analysis failed:', error);
      return [];
    }
  }

  // Reset state
  function reset() {
    state.set({
      text: '',
      result: null,
      isChecking: false,
      progress: null,
      error: null,
      selectedCorrections: {},
      isAnalyzing: false,
      aiResults: []
    });
  }

  // Initialize selected corrections
  function initializeSelectedCorrections(
    corrections: Correction[]
  ): Record<number, CorrectionStateInfo> {
    const selected: Record<number, CorrectionStateInfo> = {};
    
    corrections.forEach((correction, index) => {
      selected[index] = {
        correctionIndex: index,
        currentState: 'error',
        selectedValue: correction.corrected[0] || correction.original,
        isExceptionState: false,
        isUserEdited: false
      };
    });
    
    return selected;
  }

  return {
    // Stores
    settings: settings as Readable<GrammarCheckSettings>,
    state: state as Readable<GrammarCheckState>,
    
    // Derived stores
    isChecking,
    isAnalyzing,
    hasError,
    corrections,
    errorCount,
    processedText,
    progress,
    
    // Methods
    initialize,
    updateSettings,
    checkGrammar,
    applyCorrections,
    updateSelectedCorrection,
    toggleCorrection,
    addIgnoredWords,
    clearCache,
    getCacheStats,
    analyzeWithAI,
    reset
  };
}

// Create and export the singleton store
export const grammarStore = createGrammarStore();

// Export types for component usage
export type GrammarStore = ReturnType<typeof createGrammarStore>;
export type { GrammarCheckState, CorrectionStateInfo };